import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Save, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

function useNoticeTemplate(templateType, open, onSaved, onOpenChange) {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/notice-templates").then((r) => {
      const t = r.data.templates.find((x) => x.template_type === templateType);
      setData({ ...t, placeholders: r.data.placeholders });
      setSubject(t.subject); setBody(t.body);
    }).catch((e) => toast.error(apiErr(e)));
  }, [open, templateType]);

  const save = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body are required");
    setBusy("save");
    try {
      await api.put(`/notice-templates/${templateType}`, { subject, body });
      toast.success(`${data.label} template saved`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const reset = async () => {
    setBusy("reset");
    try {
      const { data: d } = await api.delete(`/notice-templates/${templateType}`);
      setSubject(d.subject); setBody(d.body); setData((p) => ({ ...p, is_custom: false }));
      toast.success("Restored the original wording");
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return { data, subject, setSubject, body, setBody, busy, save, reset };
}

function PlaceholderChips({ placeholders, onInsert }) {
  return (
    <div>
      <Label className="text-xs">Placeholders</Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {placeholders.map((p) => (
          <button key={p.key} type="button" onClick={() => onInsert(p.key)} title={p.desc} data-testid={`placeholder-${p.key}`}
            className="rounded-md border border-slate-200 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-300 px-2 py-1 text-[11px] font-mono text-slate-700 transition-colors">
            {`{${p.key}}`}
          </button>
        ))}
      </div>
    </div>
  );
}

export function NoticeTemplateEditor({ templateType, open, onOpenChange, onSaved }) {
  const t = useNoticeTemplate(templateType, open, onSaved, onOpenChange);
  const bodyRef = useRef(null);

  const insert = (key) => {
    const el = bodyRef.current;
    const token = `{${key}}`;
    if (!el) return t.setBody((b) => b + token);
    const s = el.selectionStart ?? t.body.length, e = el.selectionEnd ?? t.body.length;
    t.setBody(t.body.slice(0, s) + token + t.body.slice(e));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + token.length, s + token.length); });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle>Edit notice · {t.data?.label || "…"} {t.data?.is_custom && <span className="ml-2 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 align-middle" data-testid="template-custom-badge">Customized</span>}</DialogTitle>
          <DialogDescription>Your saved wording is used when you send this notice manually and by the automatic scheduled / late-fee notices. Click a placeholder to insert it.</DialogDescription>
        </DialogHeader>
        {!t.data ? <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Subject</Label>
              <Input value={t.subject} onChange={(e) => t.setSubject(e.target.value)} data-testid="template-subject" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Message</Label>
              <Textarea ref={bodyRef} value={t.body} onChange={(e) => t.setBody(e.target.value)} rows={11} className="font-mono text-[13px] leading-relaxed" data-testid="template-body" /></div>
            <PlaceholderChips placeholders={t.data.placeholders} onInsert={insert} />
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={t.reset} disabled={!!t.busy || !t.data} className="gap-2" data-testid="template-reset">
            {t.busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reset to default
          </Button>
          <Button onClick={t.save} disabled={!!t.busy || !t.data} className="gap-2 flex-1" data-testid="template-save">
            {t.busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
